import styled from "@emotion/styled";
import {
  Box,
  Button,
  Input,
  Stack,
  Typography,
  CircularProgress,
  FormControlLabel,
  Switch,
  Card,
  CardContent,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import Stepper from "../Common/Stepper";
import {
  VERTICAL,
  COMPLETE,
  NEXT,
  SALES_ORDER_STATUS,
} from "../constants/Constants";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SalesOrderItems from "../components/SalesOrder/SalesOrderItems";
import SalesSummeryCustomer from "../components/SalesOrderCustomer/SalesSummeryCustomer";
import ItemSummeryCustomer from "../components/SalesOrderCustomer/ItemSummeryCustomer";
import { useNavigate, useParams } from "react-router-dom";
import SuccessModal from "../Common/Modals/SuccessModal";
import FailedModal from "../Common/Modals/FailedModal";
import AlertToast from "../components/common/Toast";
import { apiConstants } from "../constants";
import { authHeader, getUserToken } from "../helpers";
import ProgressCircularWithBackdrop from "../Common/ProgressCircular/ProgressCircularWithBackdrop";
import { useNotification } from "../Contexts/GlobalNotificationContext";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import QuantityChangeConfirmDialog from "../components/SalesOrder/QuantityChangeConfirmDialog";
import useQuantityChangeConfirmation from "../hooks/useQuantityChangeConfirmation";
import * as XLSX from 'xlsx';

// Styled components
const StyledContainer = styled(Box)({
  width: "100%",
  border: "1px dashed #C4C4C4",
  minHeight: "200px",
});

const StyledStepperContainer = styled(Stack)({
  width: "200px",
  borderRight: "1px solid #C4C4C4",
  minHeight: "500px",
});

const StyledTableContainer = styled(Stack)({
  marginTop: "10px",
});

const StyledButton = styled(Button)(({ theme }) => ({
  color: theme.palette.neutral.light,
  height: "60px",
}));

const StyledImage = styled("img")({
  display: "block",
  maxHeight: "200px",
  maxWidth: "100%",
  objectFit: "contain",
  paddingLeft: "10px",
});

const SalesOrderCustomer = () => {
  const [sizes, setSizes] = useState([]);
  const [data, setData] = useState({ items: [] });
  const [steps, setSteps] = useState([COMPLETE]);
  const [activeStep, setActiveStep] = useState(0);
  const [mounted, setMounted] = useState(false);
  const [isSuccess, setIssuccess] = useState(false);
  const [success, setSuccess] = useState("");
  const [comments, setComments] = useState("");
  const [loading, setLoading] = useState(true);
  const [salesOrderStatus, setSalesOrderStatus] = useState({});
  const [enableCustomizeNameNumber, setEnableCustomizeNameNumber] = useState(false);
  const [itemDetailsFile, setItemDetailsFile] = useState(null);
  const [itemDetailsFileUrl, setItemDetailsFileUrl] = useState("");
  const [itemDetailsFilePath, setItemDetailsFilePath] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [excelData, setExcelData] = useState(null);

  // Quantity change confirmation hook
  const {
    showConfirmDialog,
    pendingChanges,
    totals,
    requestConfirmation,
    handleConfirm,
    handleCancel,
    prepareItemsWithConfirmation,
  } = useQuantityChangeConfirmation();

  const { addNotification } = useNotification();
  const navigator = useNavigate();
  let { id } = useParams();

  const [toastState, setToastState] = useState({
    open: false,
    vertical: "top",
    horizontal: "right",
  });
  const [toastMessage, setToastMessage] = useState("");
  const handleToastClose = () => setToastState({ open: false });

  const [file, setFile] = useState({});
  const [downloadFile, setDownloadFile] = useState([]);
  const [totalDelivery, setTotalDelivery] = useState(0);
  const [openProgressCircular, setOpenProgressCircular] = useState(false);

  // Store original quantities when data is loaded
  const [originalQuantities, setOriginalQuantities] = useState({});

  const getItem = useCallback(() => {
    const requestOptions = {
      method: "GET",
      headers: authHeader(),
    };

    fetch(`${apiConstants.API_URL}/customer/sales-orders/${id}`, requestOptions)
      .then((response) => response.text())
      .then((text) => {
        const data = text && JSON.parse(text);
        setSalesOrderStatus(data?.data?.status);
        setTotalDelivery(data?.data?.estimated_shipping_charges);
        if (data?.data?.item_details_file_path) {          
          setItemDetailsFilePath(data?.data?.item_details_file_path);
          setItemDetailsFileUrl(data?.data?.item_details_file_url);
          setEnableCustomizeNameNumber(true);
        }

        fetch(`${apiConstants.API_URL}/sizes`, requestOptions)
          .then((response) => response.text())
          .then((text) => {
            const res = text && JSON.parse(text);
            setSizes(res?.data);
            const sizesArray = res?.data;
            if (data?.data.comment) {
              setComments(data?.data.comment);
            }
            if (data?.data?.file) {
              setFile({
                image: data?.data.file,
                image_url: data?.data.file_url,
                name: data?.data.file.split("/")[2].split("_")[1],
              });
            }
            if (data?.data?.quotation?.quotation_files) {
              setDownloadFile(data?.data?.quotation?.quotation_files);
            }

            // Store original quantities for each item
            const origQtys = {};
            data?.data.items.forEach((item, index) => {
              origQtys[index] = item.qty;
            });
            setOriginalQuantities(origQtys);

            let newData = data?.data.items.map((item, itemIndex) => {
              let arrangedItems = [];
              let itemDetailsWithQuantityArrangedItems = [];
              if (item?.item_details.length !== 0 && item?.flag_name_no === 1) {
                item?.item_details?.forEach((q) => {
                  sizesArray?.forEach((s) => {
                    if (q.size?.id === s.id) {
                      const obj = {
                        ...q,
                        size_id: q.size?.id,
                        selectedSize: { id: s.id, name: s.name },
                      };
                      arrangedItems.push(obj);
                    }
                  });
                });
                itemDetailsWithQuantityArrangedItems = [
                  {
                    size_id: 0,
                    quantity: "",
                    selectedSize: { id: 0, name: "" },
                  },
                ];
              } else if (
                item?.item_details.length !== 0 &&
                item?.flag_name_no === 0
              ) {
                let sizeWithQuantity = [];
                item?.item_details?.forEach((q) => {
                  sizesArray?.forEach((s) => {
                    if (q.size?.id === s.id) {
                      const obj = {
                        size_id: q.size?.id,
                        quantity: 1,
                        selectedSize: { id: s.id, name: s.name },
                      };
                      sizeWithQuantity.push(obj);
                    }
                  });
                });
                let hash = Object.create(null);
                let result = [];
                sizeWithQuantity.forEach((o) => {
                  if (!hash[o.size_id]) {
                    hash[o.size_id] = {
                      size_id: o.size_id,
                      quantity: 0,
                      selectedSize: o.selectedSize,
                    };
                    result.push(hash[o.size_id]);
                  }
                  hash[o.size_id].quantity += +o.quantity;
                });
                itemDetailsWithQuantityArrangedItems = result;
                for (let i = 0; i < item?.qty; i++) {
                  let newObt = {
                    number: "",
                    name: "",
                    size_id: 0,
                    id: "",
                    selectedSize: { id: 0, name: "" },
                  };
                  arrangedItems.push(newObt);
                }
              } else if (item?.item_details.length === 0) {
                for (let i = 0; i < item?.qty; i++) {
                  let newObt = {
                    number: "",
                    name: "",
                    size_id: 0,
                    id: "",
                    selectedSize: { id: 0, name: "" },
                  };
                  arrangedItems.push(newObt);
                }
                itemDetailsWithQuantityArrangedItems = [
                  {
                    size_id: 0,
                    quantity: "",
                    selectedSize: { id: 0, name: "" },
                  },
                ];
              }
              let newOb = {
                ...item,
                price_id: item?.price?.id,
                color_id: item?.color?.id,
                item_details: arrangedItems,
                itemDetailsWithQuantity: itemDetailsWithQuantityArrangedItems,
                // Store original values for tracking
                originalQtyOnLoad: item.qty,
                original_unit_price_on_load: item.original_unit_price || item.unit_price,
                original_total_price_on_load: item.original_total_price || item.total_price,
              };
              return newOb;
            });
            let newDataObjec = {
              ...data?.data,
              items: newData,
            };
            setData(newDataObjec);
            const allItems = [
              ...(data?.data?.items || []),
              ...(data?.data?.custom_items || []),
            ];
            let newAppa = allItems.map((item) =>
              `${item?.style} (${item?.price?.item?.name })`
            );
            setSteps([...newAppa, ...steps]);
          });
      });
  }, [steps, id]);

  const allItems = [...(data?.items || []), ...(data?.custom_items || [])];
  const displayData = allItems[activeStep] ?? {};
  const finalStage = COMPLETE === steps[activeStep];
  const isCompleted = COMPLETE === steps[activeStep - 1];
  const [lastPages, setLastPages] = useState(false);
  const [editStatus, setEditStatus] = useState(false);
  const [disabledSubmitBtn, setDisabledSubmitBtn] = useState(false);
  const [orderItemsError, setOrderItemsError] = useState({
    status: false,
    message: "",
    index: 0,
  });

  useEffect(() => {
    if (!mounted) {
      setMounted(true);
      getItem();
    }
    const checkStageType = finalStage || isCompleted;
    setLastPages(checkStageType);
    setTimeout(() => {
      setLoading(false);
    }, 1500);
  }, [getItem, mounted, finalStage, isCompleted]);

  // Load existing file preview when sales order has item_details_file_path
  useEffect(() => {
    if (itemDetailsFileUrl && isExcelFile(itemDetailsFilePath)) {
      // Fetch and parse the Excel file from the server using the URL
      fetch(itemDetailsFileUrl)
        .then(response => response.arrayBuffer())
        .then(buffer => {
          try {
            const data = new Uint8Array(buffer);
            const workbook = XLSX.read(data, { type: 'array' });
            const firstSheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[firstSheetName];
            const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
            setExcelData(jsonData);
          } catch (error) {
            console.error('Error parsing existing Excel file:', error);
          }
        })
        .catch(error => {
          console.error('Error fetching Excel file:', error);
        });
    }
  }, [itemDetailsFilePath, itemDetailsFileUrl]);

  const isApproved = useMemo(() => {
    const approved = salesOrderStatus.keyword !== SALES_ORDER_STATUS.PENDING;
    if (approved) {
      setDisabledSubmitBtn(true);
    }
    return approved;
  }, [salesOrderStatus]);

  useEffect(() => {
    if (lastPages) {
      setDisabledSubmitBtn(false);
    }
  }, [lastPages]);

  const title = !lastPages ? `${displayData?.style} (${displayData?.price?.item?.name })`: "";
  const image = !lastPages ? displayData?.image_url : "";
  const image2 = !lastPages ? displayData?.image_2_url : "";
  const image3 = !lastPages ? displayData?.image_3_url : "";
  const buttonText = lastPages ? "CONFIRM SALES ORDER" : NEXT;

  // Check if any items have quantity changes
  const hasQuantityChanges = useMemo(() => {
    if (!data?.items) return false;
    return data.items.some((item) => {
      const originalQty = item.originalQtyOnLoad;
      const currentQty = item.qty;
      return originalQty !== undefined && originalQty !== currentQty;
    });
  }, [data?.items]);

  // Handle file upload for item details
  const handleItemDetailsFileUpload = (event) => {
    const uploadedFile = event.target.files[0];
    if (uploadedFile) {
      setPreviewLoading(true);
      setItemDetailsFile(uploadedFile);
      const fileUrl = URL.createObjectURL(uploadedFile);
      setItemDetailsFileUrl(fileUrl);
      
      // Parse Excel file if it's an Excel file
      if (isExcelFile(uploadedFile.name)) {
        parseExcelFile(uploadedFile);
      } else {
        setExcelData(null);
        setTimeout(() => setPreviewLoading(false), 500);
      }
    }
  };

  const handleRemoveItemDetailsFile = () => {
    setItemDetailsFile(null);
    setItemDetailsFileUrl("");
    setItemDetailsFilePath("");
    setExcelData(null);
  };

  // Prepare items for submission with confirmation data
  const prepareItemsForSubmission = (items, confirmedChanges = null) => {
    return items?.map((item, index) => {
      let newObt;

      // Check if this item has a confirmed quantity change
      const hasConfirmedChange = confirmedChanges?.find((c) => c.index === index);

      if (item?.flag_name_no) {
        const formateArray = item?.item_details?.map((c) => {
          return {
            number: c.number,
            name: c.name,
            size_id: c.size_id,
            id: c.id ? c.id : "",
          };
        });
        newObt = {
          ...item,
          item_details: formateArray,
        };
      } else {
        let array = [];
        item?.itemDetailsWithQuantity?.forEach((q) => {
          let obj;
          for (let i = 0; i < q.quantity; i++) {
            obj = {
              number: "",
              name: "",
              size_id: q.size_id,
              id: q.id ? q.id : "",
            };
            array.push(obj);
          }
        });
        newObt = {
          ...item,
          item_details: array,
        };
      }

      // Add quantity change confirmation data if applicable
      if (hasConfirmedChange) {
        newObt.qty_change_confirmed = true;
        newObt.original_qty = hasConfirmedChange.originalQty;
        newObt.original_order_data = hasConfirmedChange.originalData;
      }
      const extraChargesList = item?.sales_order_item_extra_charges?.map((x) => {
        return {
          id: x.id,
          qty: x.qty,          
          reference: x.reference ?? '',
          cost: x.cost,
          extra_charge_id: x.extra_charge.id,
          sales_order_item_id: x.sales_order_item_id
        };
      });
      newObt.sales_order_item_extra_charges = extraChargesList;
      delete newObt.itemDetailsWithQuantity;
      // Remove item level item_details_file_path - now at sales order level
      delete newObt.item_details_file_path;
      delete newObt.item_details_file_url;

      return newObt;
    });
  };

  // Upload item details file and return the path
  const uploadItemDetailsFileToServer = async () => {
    if (!itemDetailsFile) {
      return itemDetailsFilePath || "";
    }

    const token = getUserToken();

    // Create FormData and append the file with field name 'file'
    const formData = new FormData();
    formData.append('file', itemDetailsFile);
    formData.append('category', 'sales-order');

    const requestOptions = {
      method: "POST",
      headers: {
        Authorization: "Bearer " + token,
        // Don't set Content-Type - browser will set it with boundary
      },
      body: formData,
    };

    try {
      const response = await fetch(
        `${apiConstants.API_URL}/file/sales-order`,
        requestOptions
      );
      const text = await response.text();
      const responseData = text && JSON.parse(text);

      if (!response.ok) {
        throw new Error(responseData?.error || "Failed to upload file");
      }

      return responseData?.data?.path || "";
    } catch (error) {
      console.error("Error uploading item details file:", error);
      throw error;
    }
  };

  // Submit the sales order
  const submitSalesOrderWithItems = async (preparedItems) => {
    const {
      order_method,
      organization,
      quotation,
      status,
      estimated_shipping_charges,
    } = data;

    let imagePath = "";

    if (status.id === 2) {
      setToastMessage("Order is already accepted");
      setToastState({
        open: true,
        vertical: "top",
        horizontal: "right",
      });
      setOpenProgressCircular(false);
      return;
    }

    const token = getUserToken();

    try {
      // Upload item details file if enabled and file selected
      let uploadedItemDetailsPath = "";
      if (enableCustomizeNameNumber) {
        uploadedItemDetailsPath = await uploadItemDetailsFileToServer();
      }

      const submitOrder = (imgPath) => {
        let totalShipping = 0;
        preparedItems.forEach(item => {
          totalShipping += item?.estimated_shipping_charges || 0;
        });
        let reqObe = {
          order_method_id: order_method?.id,
          invoice_id: null,
          organization_id: organization?.id,
          quotation_id: quotation?.id,
          status_id: 2,
          items: preparedItems,
          comment: comments,
          estimated_shipping_charges: totalShipping,
          item_details_file_path: uploadedItemDetailsPath,
        };

        if (imgPath !== "") {
          reqObe.file = imgPath;
        }

        const requestOpts = {
          method: "PUT",
          headers: authHeader(),
          body: JSON.stringify(reqObe),
        };

        fetch(
          `${apiConstants.API_URL}/customer/sales-orders/${id}`,
          requestOpts
        ).then((response) => {
          response.text().then((text) => {
            const responseData = text && JSON.parse(text);
            if (!response.ok) {
              if (response.status === 401) {
                // logout()
              }
              const error = (responseData && responseData.error) || response.statusText;
              const errorMessage = Object.values(error).map(
                (errorKey) => errorKey[0]
              );
              setOpenProgressCircular(false);
              setToastMessage(errorMessage);
              setToastState({
                open: true,
                vertical: "top",
                horizontal: "right",
              });
            } else {
              setOpenProgressCircular(false);
              setSuccess(responseData?.data?.id);
              setIssuccess(true);
              addNotification({
                message: {
                  title: "Sales Order Accepted Successfully!",
                  body: `Sales Order #${responseData?.data?.id} is accepted successfully. Your account manager will contact and guide you through the next steps`,
                  extra: "Thank you for your order.",
                },
                type: "success-model",
              });
              setActiveStep(steps.length);
              setDisabledSubmitBtn(true);
              navigator("/sales-orders");
            }
          });
        });
      };

      if (file?.image_url) {
        const requestImageOptions = {
          method: "POST",
          headers: {
            Authorization: "Bearer " + token,
          },
          body: file?.image,
        };
        fetch(
          `${apiConstants.API_URL}/file/sales-order`,
          requestImageOptions
        ).then((response) => {
          response.text().then((text) => {
            const imageData = text && JSON.parse(text);
            if (!response.ok) {
              const error = (imageData && imageData.error) || response.statusText;
              const errorMessage = Object.values(error).map(
                (errorKey) => errorKey[0]
              );
              setOpenProgressCircular(false);
              setToastMessage(errorMessage[0]);
              setToastState({
                open: true,
                vertical: "top",
                horizontal: "right",
              });
            } else {
              submitOrder(imageData?.data?.path);
            }
          });
        });
      } else {
        submitOrder(imagePath);
      }
    } catch (error) {
      setOpenProgressCircular(false);
      setToastMessage(error.message || "Failed to upload file");
      setToastState({
        open: true,
        vertical: "top",
        horizontal: "right",
      });
    }
  };

  // Main submit action - checks for quantity changes first
  const submitAction = () => {
    setOpenProgressCircular(true);

    // Check if there are quantity changes that need confirmation
    const needsConfirmation = requestConfirmation(data.items, (confirmedChanges) => {
      // User confirmed the changes - prepare items with confirmation data
      const preparedItems = prepareItemsForSubmission(data.items, confirmedChanges);
      submitSalesOrderWithItems(preparedItems);
    });

    // If no confirmation needed, submit directly
    if (!needsConfirmation) {
      const preparedItems = prepareItemsForSubmission(data.items);
      submitSalesOrderWithItems(preparedItems);
    } else {
      // Dialog is shown, stop the progress indicator
      setOpenProgressCircular(false);
    }
  };

  // Handle dialog confirmation
  const onDialogConfirm = () => {
    setOpenProgressCircular(true);
    handleConfirm();
  };

  const nextAction = () => {
    let length = steps.length;
    if (activeStep < length - 1) {
      setActiveStep(activeStep + 1);
      setEditStatus(true);
      return;
    }

    if (activeStep === length - 1) {
      submitAction();
    }
  };

  const preAction = () => {
    setActiveStep(activeStep - 1);
  };

  const updateComments = (e) => {
    const { target } = e;
    setComments(target?.value);
  };

  const downloadUsingAnchorElement = async (index) => {
    const anchor = document.createElement("a");
    anchor.href = downloadFile[index].file_url;
    anchor.download = "*";
    document.body.appendChild(anchor);
    anchor.click();
  };

  // Handle quantity change notification from SalesOrderItems
  const handleQuantityChange = (stepIndex, changeInfo) => {
    console.log("Quantity changed for step:", stepIndex, changeInfo);
  };

  // Get file extension for preview logic
  const getFileExtension = (filename) => {
    return filename?.split('.').pop()?.toLowerCase() || '';
  };

  const isImageFile = (filename) => {
    const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'];
    return imageExtensions.includes(getFileExtension(filename));
  };

  const isPdfFile = (filename) => {
    return getFileExtension(filename) === 'pdf';
  };

  const isExcelFile = (filename) => {
    const excelExtensions = ['xlsx', 'xls', 'csv'];
    return excelExtensions.includes(getFileExtension(filename));
  };

  // Parse Excel file for preview
  const parseExcelFile = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        
        // Get first sheet
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        
        // Convert to JSON
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
        
        setExcelData(jsonData);
        setPreviewLoading(false);
      } catch (error) {
        console.error('Error parsing Excel file:', error);
        setExcelData(null);
        setPreviewLoading(false);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  return (
    <>
      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "400px",
          }}
        >
          <CircularProgress size={50} />
        </Box>
      ) : (
        <Stack direction={"row"} spacing={5}>
          <StyledStepperContainer
            direction={"column"}
            sx={{ minWidth: "160px" }}
          >
            <Stepper
              activeStep={activeStep}
              orientation={VERTICAL}
              steps={isApproved ? steps.slice(0, -1) : steps}
              alternativeLabel={false}
            />
          </StyledStepperContainer>
          <Stack style={{ textAlign: "left", width: "100%" }} spacing={4}>
            <Typography fontSize={"28px"} fontWeight={500}>
              {title}
            </Typography>
            {!lastPages && (
              <StyledContainer
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {!image ? <div></div> : <StyledImage src={image} />}
                {!image2 ? <div></div> : <StyledImage src={image2} />}
                {!image3 ? <div></div> : <StyledImage src={image3} />}
              </StyledContainer>
            )}

            <StyledTableContainer spacing={4}>
              {!lastPages && (
                <SalesOrderItems
                  sizes={sizes}
                  item_details={displayData?.item_details}
                  displayData={displayData}
                  setData={setData}
                  data={data}
                  activeStep={activeStep}
                  editStatus={editStatus}
                  error={orderItemsError}
                  setError={setOrderItemsError}
                  setDisabledSubmitBtn={setDisabledSubmitBtn}
                  readOnly={isApproved}
                  onQuantityChange={handleQuantityChange}
                  hideFileUpload={true}
                />
              )}

              {lastPages && !isApproved && (
                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Customize Name and Number
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      If you need to add custom names and numbers to your order, enable this option and upload your file.
                    </Typography>
                    
                    <FormControlLabel
                      control={
                        <Switch
                          checked={enableCustomizeNameNumber}
                          onChange={(e) => setEnableCustomizeNameNumber(e.target.checked)}
                          color="primary"
                        />
                      }
                      label="Enable Customize Name and Number"
                    />

                    {enableCustomizeNameNumber && (
                      <Box sx={{ mt: 2 }}>
                        <Stack spacing={2}>
                          <Box>
                            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                              Template File:
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
                              Please download the template file and fill in the details.
                            </Typography>
                            <Button
                              variant="outlined"
                              href="/downloads/Sales Order Numbers and Names Template.xlsx"
                              sx={{ mb: 2 }}
                            >
                              Download Template
                            </Button>
                          </Box>
                          
                          <Box>
                            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                              Upload File:
                            </Typography>
                            {!itemDetailsFileUrl && !itemDetailsFilePath ? (
                              <Button
                                variant="outlined"
                                component="label"
                                startIcon={<CloudUploadIcon />}
                              >
                                Upload Name/Number File
                                <input
                                  type="file"
                                  hidden
                                  accept=".xlsx,.xls,.csv,.pdf"
                                  onChange={handleItemDetailsFileUpload}
                                />
                              </Button>
                            ) : (
                              <>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 2,
                                    p: 2,
                                    border: "1px solid #e0e0e0",
                                    borderRadius: 1,
                                  }}
                                >
                                  <InsertDriveFileIcon color="primary" />
                                  <Typography sx={{ flex: 1 }}>
                                    {itemDetailsFile?.name || itemDetailsFilePath?.split("/").pop()}
                                  </Typography>
                                  <Button
                                    size="small"
                                    color="error"
                                    startIcon={<DeleteIcon />}
                                    onClick={handleRemoveItemDetailsFile}
                                  >
                                    Remove
                                  </Button>
                                </Box>
                                
                                {/* File Preview */}
                                {previewLoading ? (
                                  <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                                    <CircularProgress size={30} />
                                  </Box>
                                ) : (
                                  <Box sx={{ mt: 2, border: "1px solid #e0e0e0", borderRadius: 1, p: 2 }}>
                                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                                      Preview:
                                    </Typography>
                                    {isImageFile(itemDetailsFile?.name || itemDetailsFilePath) ? (
                                      <img
                                        src={itemDetailsFileUrl}
                                        alt="File preview"
                                        style={{
                                          maxWidth: "100%",
                                          maxHeight: "400px",
                                          objectFit: "contain",
                                          display: "block",
                                        }}
                                      />
                                    ) : isExcelFile(itemDetailsFile?.name || itemDetailsFilePath) ? (
                                      <Box sx={{ overflowX: "auto", maxHeight: "400px", overflowY: "auto" }}>
                                        {excelData && excelData.length > 0 ? (
                                          <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                            <tbody>
                                              {excelData.map((row, rowIndex) => (
                                                <tr key={rowIndex} style={{ backgroundColor: rowIndex === 0 ? "#f5f5f5" : "white" }}>
                                                  {row.map((cell, cellIndex) => (
                                                    <td
                                                      key={cellIndex}
                                                      style={{
                                                        border: "1px solid #ddd",
                                                        padding: "8px",
                                                        fontWeight: rowIndex === 0 ? "bold" : "normal",
                                                      }}
                                                    >
                                                      {cell !== null && cell !== undefined ? String(cell) : ""}
                                                    </td>
                                                  ))}
                                                </tr>
                                              ))}
                                            </tbody>
                                          </table>
                                        ) : (
                                          <Typography variant="body2" color="text.secondary">
                                            No data to preview
                                          </Typography>
                                        )}
                                      </Box>
                                    ) : isPdfFile(itemDetailsFile?.name || itemDetailsFilePath) ? (
                                      <Box>
                                        <iframe
                                          src={itemDetailsFileUrl}
                                          style={{
                                            width: "100%",
                                            height: "400px",
                                            border: "none",
                                          }}
                                          title="PDF Preview"
                                        />
                                      </Box>
                                    ) : (
                                      <Box sx={{ textAlign: "center", py: 3 }}>
                                        <InsertDriveFileIcon sx={{ fontSize: 60, color: "text.secondary" }} />
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                          Preview not available for this file type
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                          {itemDetailsFile?.name || itemDetailsFilePath?.split("/").pop()}
                                        </Typography>
                                      </Box>
                                    )}
                                  </Box>
                                )}
                              </>
                            )}
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                              Accepted formats: .xlsx, .xls, .csv, .pdf
                            </Typography>
                          </Box>
                        </Stack>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              )}

              {lastPages && isApproved && itemDetailsFilePath && (
                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Customize Name and Number File
                    </Typography>
                    
                    <Box sx={{ mt: 2 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                          p: 2,
                          border: "1px solid #e0e0e0",
                          borderRadius: 1,
                          mb: 2,
                        }}
                      >
                        <InsertDriveFileIcon color="primary" />
                        <Typography sx={{ flex: 1 }}>
                          {itemDetailsFilePath?.split("/").pop()}
                        </Typography>
                        <Button
                          size="small"
                          variant="outlined"
                          href={itemDetailsFileUrl}
                          download
                        >
                          Download
                        </Button>
                      </Box>
                      
                      {/* File Preview for Approved Orders */}
                      <Box sx={{ border: "1px solid #e0e0e0", borderRadius: 1, p: 2 }}>
                        <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                          Preview:
                        </Typography>
                        {isImageFile(itemDetailsFilePath) ? (
                          <img
                            src={itemDetailsFileUrl}
                            alt="File preview"
                            style={{
                              maxWidth: "100%",
                              maxHeight: "400px",
                              objectFit: "contain",
                              display: "block",
                            }}
                          />
                        ) : isExcelFile(itemDetailsFilePath) ? (
                          <Box sx={{ overflowX: "auto", maxHeight: "400px", overflowY: "auto" }}>
                            {excelData && excelData.length > 0 ? (
                              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                <tbody>
                                  {excelData.map((row, rowIndex) => (
                                    <tr key={rowIndex} style={{ backgroundColor: rowIndex === 0 ? "#f5f5f5" : "white" }}>
                                      {row.map((cell, cellIndex) => (
                                        <td
                                          key={cellIndex}
                                          style={{
                                            border: "1px solid #ddd",
                                            padding: "8px",
                                            fontWeight: rowIndex === 0 ? "bold" : "normal",
                                          }}
                                        >
                                          {cell !== null && cell !== undefined ? String(cell) : ""}
                                        </td>
                                      ))}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                No data to preview
                              </Typography>
                            )}
                          </Box>
                        ) : isPdfFile(itemDetailsFilePath) ? (
                          <Box>
                            <iframe
                              src={itemDetailsFileUrl}
                              style={{
                                width: "100%",
                                height: "400px",
                                border: "none",
                              }}
                              title="PDF Preview"
                            />
                          </Box>
                        ) : (
                          <Box sx={{ textAlign: "center", py: 3 }}>
                            <InsertDriveFileIcon sx={{ fontSize: 60, color: "text.secondary" }} />
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                              Preview not available for this file type
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {itemDetailsFilePath?.split("/").pop()}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              )}

              {!isApproved && (
                <Stack>
                  <Typography style={{ fontSize: "20px", fontWeight: 700 }}>
                    Please review your order details on the right side.
                  </Typography>
                  <Typography style={{ fontSize: "15px" }}>
                    If everything looks correct, click the "CONFIRM SALES ORDER"
                    button.
                  </Typography>
                  {hasQuantityChanges && (
                    <Typography 
                      style={{ 
                        fontSize: "14px", 
                        color: "#ff9800",
                        marginTop: "10px",
                        fontWeight: 500,
                      }}
                    >
                      ⚠️ You have made quantity changes. You will be asked to confirm these changes before submitting.
                    </Typography>
                  )}
                </Stack>
              )}
            </StyledTableContainer>
          </Stack>

          <Stack spacing={2}>
            {downloadFile && downloadFile?.length !== 0 && (
              <Box>
                <Typography>Pattern File</Typography>
                {downloadFile?.map((i, index) => (
                  <Box
                    key={i?.id}
                    sx={{
                      display: "flex",
                      padding: "5px",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Box sx={{ display: "flex" }}>
                      <InsertDriveFileIcon
                        color="primary"
                        sx={{ marginRight: "5px" }}
                      />
                      <Typography variant="span">{i?.name}</Typography>
                    </Box>
                    <Button onClick={() => downloadUsingAnchorElement(index)}>
                      Download File
                    </Button>
                  </Box>
                ))}
              </Box>
            )}

            {displayData && !lastPages && (
              <ItemSummeryCustomer
                {...displayData}
                originalQty={displayData?.originalQtyOnLoad}
                shippingEnabled={data?.organization?.shipping_enabled ?? true}
              />
            )}

            <SalesSummeryCustomer
              data={data}
              displayData={displayData}
              activeStep={activeStep}
              totalDelivery={totalDelivery}
            />

            <StyledButton
              variant="contained"
              color="success"
              endIcon={<ArrowForwardIcon />}
              onClick={nextAction}
              disabled={disabledSubmitBtn || (isApproved && lastPages)}
            >
              {buttonText}
            </StyledButton>
            {activeStep > 0 && (
              <Button startIcon={<ArrowBackIcon />} onClick={preAction}>
                BACK
              </Button>
            )}
          </Stack>
          
          {/* Quantity Change Confirmation Dialog */}
          <QuantityChangeConfirmDialog
            open={showConfirmDialog}
            onClose={handleCancel}
            onConfirm={onDialogConfirm}
            changes={pendingChanges}
            totals={totals}
          />
          
          <ProgressCircularWithBackdrop open={openProgressCircular} />
          <FailedModal />
          <AlertToast
            toastMessage={toastMessage}
            toastState={toastState}
            handleClose={handleToastClose}
          />
        </Stack>
      )}
    </>
  );
};

export default SalesOrderCustomer;
